import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, ChevronDown, Inbox, RotateCcw, Search } from '../../../icons';
import { getApprovalStats, getDocumentCounts, listDocuments, listInvolvedDocuments, type ApprovalStatsDto } from '../../../api/approval';
import { listDocTypes, type DocTypeDto } from '../../../api/approval-doc-type';
import { useApprovalStore } from '../../../store/approvalStore';
import { useIsAdminViewer } from '../../../lib/menu-catalog';
import type { ApprovalBoxCounts, ApprovalDocumentSummary, DocumentView } from '../../../types/approval';
import { Avatar } from '../../../components/shared/Avatar/Avatar';
// 손짠 머리 → 정본 PageHeader · 경로 · 범위 칩(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { PageHeader } from '../../../components/shared';
import { crumbsFor, scopeFor } from '../../../lib/page-crumbs';
import {
  BOX_GROUP, BOX_LABEL, INVOLVED_BOX_STATUS, NOT_READY_TEXT, VIEWS_TO_LOAD, countFromServer, docsForBox, draftedOn, isBoxKey, mmdd,
  type BoxKey, type BoxSource,
} from './approval-box-types';
// 툴바 · 조건 상세 · 칩 (2026-09-17 · 결재함 개편) — 기존 규칙은 안 건드리고 더하기만 한다.
import { ApprovalFilterBar, ApprovalFilterChips, PendingTag } from './ApprovalFilterBar';
import {
  CLEAR_FILTERS_LABEL, EMPTY_FILTERS, clearFilters, hasActiveFilters, type ApprovalFilters,
} from './approval-filters';
import './approval-box.css';
// «상태» 칸 — 강조는 «내 차례» 한 곳(2026-10-18 · web 레인 W10-27 · 렌즈 Carbon · 결정 D10)
import './approval-box-status.css';
import { approvalStatusMark } from '../../../lib/approval-status';

/**
 * 결재함 (2026-09-13 · 과제 L1 · `/approval/box`).
 *
 * 시안 `axi-approval-status` v2(사용자 확인 — 그대로 반영):
 *   KPI 4(내 결재 대기 · 평균 처리 시간 · 이번 달 결재 · **결재 대기함**) → [결재함 트리 | 전자결재 문서 표 + 일괄 막대].
 * ★ 행을 누르면 **문서 화면으로 이동**한다(`/approval/doc/:id` — 목록 옆 상세 패널은 없다, 직원관리와 같은 패턴).
 * ★ 함은 서버 문서함 넷(받은 · 올린 · 임시 · 참고)을 읽어 트리 마디로 가른다 — 새 서버 라우트 없음.
 * ★ 서버에 없는 값(기안자 이름 · 주별 추이 · 일괄 처리 · 부서 결재함 문서)은 자리만 그린다 — `approval-box-types.ts`.
 * ★★ 2026-09-14(서버 #1247 · 계약 §20) — 기안자 이름 · 팀 · 사진(`drafter`) · 서버 검색(`q`) · 함별 건수(`counts`)를 잇는다.
 *    옛 서버(칸 없음 · 건수 실패)면 종전대로 자리표시 · 화면 거르기 · 목록에서 센 수로 선다.
 * ★ 종전 문서함 넷(`/approval/pending` …) · 결재 홈은 그대로다(옛 주소를 여기로 넘기는 일은 L2).
 */
type Loaded = Partial<Record<DocumentView, ApprovalDocumentSummary[] | null>>;

const GROUPS: ReadonlyArray<{ label: '결재하기' | '기안 문서함' | '참고 · 열람'; boxes: BoxKey[] }> = [
  { label: '결재하기', boxes: ['wait', 'progress', 'done', 'reject'] },
  { label: '기안 문서함', boxes: ['temp', 'sent'] },
  { label: '참고 · 열람', boxes: ['ref', 'dept'] },
];

function daysSince(iso: string): number | null {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000)) + 1;
}

/**
 * ⚠ **여기는 대시보드(`board/relative-time.ts`)와 «다른 말»을 쓴다 — 빠뜨린 것이 아니다.**
 *
 * 2026-09-22 에 대시보드 위젯 셋의 상대 시각을 `board/relative-time.ts` 한 곳으로 모았는데,
 * **이 함수는 일부러 두었다.** 말이 다르기 때문이다:
 * ```
 *   board/relative-time.ts   방금 · N분 전 · N시간 전 · 9/15      ← 하루 넘으면 «날짜»
 *   여기(결재함)              1분 전 · N시간 전 · 어제 · N일 전     ← 「방금」 없음 · 「어제」 있음 · 계속 «N일 전»
 * ```
 * 합치면 **결재함의 화면 글자가 바뀐다.** 그것은 리팩터링이 아니라 **별개 결정**이라
 * 사용자 확인이 필요하다 — 통일하려면 그때 이 주석을 지우고 옮긴다.
 */
function ago(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const min = Math.floor((Date.now() - t) / 60_000);
  if (min < 60) return `${Math.max(1, min)}분 전`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}시간 전`;
  const d = Math.floor(h / 24);
  return d === 1 ? '어제' : `${d}일 전`;
}

export function ApprovalBoxPage(): JSX.Element {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isAdmin = useIsAdminViewer();
  const bump = useApprovalStore((s) => s.lastApprovalStatusBump);

  const boxParam = params.get('box');
  const box: BoxKey = isBoxKey(boxParam) ? boxParam : 'wait';

  const [loaded, setLoaded] = useState<Loaded>({});
  const [stats, setStats] = useState<ApprovalStatsDto | null>(null);
  const [types, setTypes] = useState<DocTypeDto[]>([]);
  const [open, setOpen] = useState<Record<string, boolean>>({ '결재하기': true, '기안 문서함': true, '참고 · 열람': true });
  const [treeQ, setTreeQ] = useState('');
  /**
   * 조건 (2026-09-17) — 검색어를 **여기로 합쳤다**. `q` 는 종전과 같은 값이라
   * 아래 `term` · 디바운스 · 화면 거르기는 **한 줄도 안 바뀐다**(회귀 금지 기준선).
   * ★ 문서 화면 머리의 찾기(L3)가 `?q=` 로 넘겨주는 것도 그대로 — 처음 한 번만 받는다.
   */
  const [filters, setFilters] = useState<ApprovalFilters>(() => ({ ...EMPTY_FILTERS, q: params.get('q') ?? '' }));
  const q = filters.q;
  const [condOpen, setCondOpen] = useState(false);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [counts, setCounts] = useState<ApprovalBoxCounts | null>(null);
  const [hits, setHits] = useState<Loaded>({});

  useEffect(() => {
    let alive = true;
    for (const v of VIEWS_TO_LOAD) {
      void listDocuments(v, { limit: 50 })
        .then((r) => { if (alive) setLoaded((s) => ({ ...s, [v]: r.items })); })
        .catch(() => { if (alive) setLoaded((s) => ({ ...s, [v]: null })); });
    }
    // 함별 건수(§20.3) — 못 읽으면(옛 서버) 목록에서 센 수로 선다.
    void getDocumentCounts()
      .then((c) => { if (alive) setCounts(c); })
      .catch(() => { if (alive) setCounts(null); });
    return () => { alive = false; };
    // SSE 로 결재 상태가 바뀌면 함께 다시 읽는다(셸 배지와 같은 규칙).
  }, [bump]);

  useEffect(() => {
    let alive = true;
    void listDocTypes().then((t) => { if (alive) setTypes(t); }).catch(() => { if (alive) setTypes([]); });
    // 결재 통계는 담당자 전용(서버 403) — 직원에게는 부르지 않고 자리만 그린다.
    if (isAdmin) void getApprovalStats('month').then((s) => { if (alive) setStats(s); }).catch(() => { if (alive) setStats(null); });
    return () => { alive = false; };
  }, [isAdmin]);

  useEffect(() => { setChecked(new Set()); }, [box]);

  // 서버 검색(§20.2 · 제목 · 문서번호 · 기안자 이름) — 치는 동안 300ms 모았다가 문서함 넷을 `q` 로 다시 읽는다.
  // ★ 옛 서버는 모르는 `q` 를 조용히 버리므로(스키마 removeAdditional) 아래 화면 거르기도 그대로 둔다 — AND 라 결과는 같다.
  const term = q.trim().slice(0, 100);
  useEffect(() => {
    setHits({});
    if (!term) return undefined;
    let alive = true;
    const t = window.setTimeout(() => {
      for (const v of VIEWS_TO_LOAD) {
        void listDocuments(v, { limit: 50, q: term })
          .then((r) => { if (alive) setHits((s) => ({ ...s, [v]: r.items })); })
          .catch(() => { if (alive) setHits((s) => ({ ...s, [v]: null })); });
      }
    }, 300);
    return () => { alive = false; window.clearTimeout(t); };
  }, [term, bump]);

  // 진행 중 · 완료 · 반려(§20.3 `view=involved&status`) — 그 마디를 고를 때만 읽는다(요청 한 번 · 검색이면 `q` 도).
  // ★ **옛 서버(400 · 404)** 로 판정되면 이 화면 동안은 종전 화면 가르기로 선다 — 수 · 목록이 같이 넘어간다.
  // ★ 2026-09-14(리뷰 #1269) — 429 · 5xx · 끊김은 **굳히지 않는다**: 그 목록만 이번에 종전 가르기로 보이고(`items: null`),
  //   트리 수는 서버 값 그대로 · 마디를 옮기거나 검색어 · 결재 상태가 바뀌면 다시 묻는다.
  const involvedStatus = INVOLVED_BOX_STATUS[box];
  const [involved, setInvolved] = useState<{ key: string; items: ApprovalDocumentSummary[] | null } | null>(null);
  const [involvedFailed, setInvolvedFailed] = useState(false);
  useEffect(() => {
    if (!involvedStatus || involvedFailed) return undefined;
    let alive = true;
    const key = `${involvedStatus}|${term}`;
    const t = window.setTimeout(() => {
      void listInvolvedDocuments(involvedStatus, term ? { limit: 50, q: term } : { limit: 50 })
        .then((r) => { if (alive) setInvolved({ key, items: r.items }); })
        .catch((err: unknown) => {
          if (!alive) return;
          const status = (err as { response?: { status?: number } } | null)?.response?.status;
          if (status === 400 || status === 404) setInvolvedFailed(true);
          else setInvolved({ key, items: null });
        });
    }, term ? 300 : 0);
    return () => { alive = false; window.clearTimeout(t); };
  }, [involvedStatus, term, bump, involvedFailed]);

  const typeName = useMemo(() => {
    const m = new Map(types.map((t) => [t.code, t.name]));
    return (code: string) => m.get(code) ?? code;
  }, [types]);

  const loading = VIEWS_TO_LOAD.some((v) => loaded[v] === undefined);
  const failed = !loading && VIEWS_TO_LOAD.every((v) => loaded[v] === null);
  const src: BoxSource = loaded;
  // 트리 수 — 서버 함별 건수로 셀 수 있는 마디는 그 값(목록 50건 창과 달리 전부 센다), 나머지는 목록에서 센다.
  const serverCount = (b: BoxKey) => countFromServer(b, counts, !involvedFailed);
  const count = (b: BoxKey) => serverCount(b) ?? docsForBox(b, src).length;
  // 검색 중이면 서버가 거른 목록 — 아직 안 왔거나 실패한 문서함은 받아 둔 목록을 화면에서 거른다.
  const listSrc: BoxSource = term
    ? Object.fromEntries(VIEWS_TO_LOAD.map((v) => [v, hits[v] ?? loaded[v]]))
    : src;

  const wait = docsForBox('wait', src);
  const waitCount = counts?.wait ?? wait.length;
  const byInvolved = !!involvedStatus && !involvedFailed;
  // 검색어를 바꾸는 동안은 직전 목록을 둔다(아래 화면 거르기가 AND 로 좁힌다) — 마디가 바뀌면 비운다.
  const involvedItems = byInvolved && involved?.key.startsWith(`${involvedStatus}|`) ? involved.items : undefined;
  // `null` = 이번에 일시 실패(429 · 5xx) — 그 목록만 종전 화면 가르기로 보인다(굳히지 않는다).
  const listLoading = byInvolved && involvedItems !== null ? involvedItems === undefined : loading;
  const inBox = (byInvolved && involvedItems !== null ? (involvedItems ?? []) : docsForBox(box, listSrc))
    .slice()
    .sort((a, b) => new Date(draftedOn(b)).getTime() - new Date(draftedOn(a)).getTime());
  const needle = term.toLowerCase();
  const shown = needle
    ? inBox.filter((d) => d.title.toLowerCase().includes(needle) || (d.docNo ?? '').toLowerCase().includes(needle)
      || (d.drafter?.name ?? '').toLowerCase().includes(needle))
    : inBox;

  const pick = (b: BoxKey) => setParams(b === 'wait' ? {} : { box: b }, { replace: false });
  const toggle = (id: string) => setChecked((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  // ① 내 결재 대기 — 양식별 막대(많은 순 셋)
  const byType = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of wait) m.set(d.type, (m.get(d.type) ?? 0) + 1);
    return [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3);
  }, [wait]);
  const waitMax = Math.max(1, ...byType.map(([, n]) => n));
  const oldest = wait.reduce<number | null>((acc, d) => {
    const n = daysSince(draftedOn(d));
    return n === null ? acc : Math.max(acc ?? 0, n);
  }, null);
  const urgent = wait.filter((d) => d.isUrgent).length;

  // ③ 이번 달 결재 — 승인 비율 링(담당자 통계)
  const decided = stats ? stats.summary.approved + stats.summary.rejected : 0;
  const approvedPct = stats && decided > 0 ? Math.round((stats.summary.approved / decided) * 100) : null;

  const pathText = box === 'all' ? '전체 문서' : `${BOX_GROUP[box]} › ${BOX_LABEL[box]}`;
  const matchTree = (label: string) => !treeQ.trim() || label.includes(treeQ.trim());

  /** 걸린 조건을 「무엇이」로 말하는 칩 — 숫자 배지(몇 개)와 짝이다. */
  const STATUS_LABEL: Record<string, string> = { IN_PROGRESS: '진행 중', APPROVED: '완료', REJECTED: '반려' };
  const chips = useMemo(() => {
    const out: Array<{ key: string; label: string; onRemove: () => void }> = [];
    if (filters.status) {
      out.push({
        key: 'status',
        label: `상태 · ${STATUS_LABEL[filters.status] ?? filters.status}`,
        onRemove: () => setFilters((f) => ({ ...f, status: null })),
      });
    }
    if (filters.deptBoxId) {
      out.push({
        key: 'deptBoxId',
        label: '부서 결재함',
        onRemove: () => setFilters((f) => ({ ...f, deptBoxId: null })),
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.deptBoxId]);

  return (
    <div className="apb-root">
      {/* ★ 검색 입력은 **문서 목록 위 툴바로 옮겼다**(2026-09-17 사용자 요구 ① · 정본 P3
          「필터바 → 표 → 페이징 3층」). 종전에는 여기 머리 오른쪽 끝에 있어서, 거르려는 표와
          가로 1180px · 세로 300px 떨어져 있었고 그 사이를 KPI 카드 넷이 막고 있었다. */}
      <PageHeader
        breadcrumb={crumbsFor('/approval/box')}
        scope={scopeFor('/approval/box')}
        title="결재함"
      />

      {failed && <p className="apb-alert" role="alert"><AlertCircle aria-hidden />결재 문서를 불러오지 못했습니다.</p>}

      <section className="apb-kpis" aria-label="결재 지표">
        {/* ① 내 결재 대기 */}
        <article className="apb-card apb-kpi">
          <div className="apb-vh">
            <span className="apb-lb">내 결재 대기</span>
            {urgent > 0 && <span className="apb-badge apb-badge--bad">긴급 {urgent}</span>}
            <details className="apb-tv"><summary>양식별 표</summary>
              <table><thead><tr><th>양식</th><th className="r">문서</th></tr></thead>
                <tbody>{byType.length === 0
                  ? <tr><td colSpan={2}>없음</td></tr>
                  : byType.map(([t, n]) => <tr key={t}><td>{typeName(t)}</td><td className="r">{n}건</td></tr>)}</tbody></table>
            </details>
          </div>
          <div className="apb-vv"><b className="num">{loading && !counts ? '—' : waitCount}<small>건</small></b>
            <span className="apb-vs">{oldest ? `내 차례 · 가장 오래된 문서 ${oldest}일째` : '내 차례인 문서'}</span></div>
          {byType.length > 0 ? (
            <div className="apb-bars" role="img" aria-label={`양식별 결재 대기: ${byType.map(([t, n]) => `${typeName(t)} ${n}건`).join(', ')}`}>
              {byType.map(([t, n]) => (
                <div className="apb-hb" key={t}><span className="bl">{typeName(t)}</span><span className="bt"><i style={{ width: `${(n / waitMax) * 100}%` }} /></span><span className="bv">{n}건</span></div>
              ))}
            </div>
          ) : <div className="apb-empty-box" role="note"><b>없음</b><span>내 차례인 문서가 없습니다</span></div>}
        </article>

        {/* ② 평균 처리 시간 */}
        <article className="apb-card apb-kpi">
          <div className="apb-vh"><span className="apb-lb">평균 처리 시간</span><details className="apb-tv"><summary>주별 처리 표</summary>
            <table><tbody><tr><td>{NOT_READY_TEXT}</td></tr></tbody></table></details></div>
          <div className="apb-vv"><b className="num">{stats?.summary.avgDays ?? '—'}<small>일</small></b>
            <span className="apb-vs">상신부터 최종 승인까지 · 회사 전체{stats ? ' · 이번 달' : ''}</span></div>
          <div className="apb-empty-box" role="note"><b>{NOT_READY_TEXT}</b><span>{isAdmin ? '주별 처리 시간 추이' : '결재 통계는 회사 관리자에게 보입니다'}</span></div>
        </article>

        {/* ③ 이번 달 결재 */}
        <article className="apb-card apb-kpi">
          <div className="apb-vh"><span className="apb-lb">이번 달 결재</span><details className="apb-tv"><summary>결과별 표</summary>
            <table><thead><tr><th>결과</th><th className="r">문서</th></tr></thead><tbody>
              <tr><td>승인</td><td className="r">{stats ? `${stats.summary.approved}건` : '—'}</td></tr>
              <tr><td>반려</td><td className="r">{stats ? `${stats.summary.rejected}건` : '—'}</td></tr>
              <tr><td>회수</td><td className="r">{NOT_READY_TEXT}</td></tr>
            </tbody></table></details></div>
          <div className="apb-vv"><b className="num">{stats?.summary.drafted ?? '—'}<small>건</small></b>
            <span className="apb-vs">{stats ? `회사 전체 · ${stats.from} ~ ${stats.to}` : '회사 전체 · 이번 달'}</span></div>
          {stats ? (
            <div className="apb-ringrow">
              <div className="apb-ring" role="img" aria-label={`이번 달 결재 ${stats.summary.drafted}건 중 승인 ${stats.summary.approved}건`}>
                <svg viewBox="0 0 84 84"><circle className="trk" cx="42" cy="42" r="34" /><circle className="val" cx="42" cy="42" r="34" pathLength={100} strokeDasharray={`${approvedPct ?? 0} 100`} transform="rotate(-90 42 42)" /></svg>
                <span className="rc"><b>{approvedPct === null ? '—' : `${approvedPct}%`}</b><span>승인</span></span>
              </div>
              <ul className="apb-res">
                <li><span>승인</span><b className="num">{stats.summary.approved}건</b></li>
                <li><span>반려</span><b className="num">{stats.summary.rejected}건</b></li>
                <li><span>회수</span><b>—</b></li>
              </ul>
            </div>
          ) : <div className="apb-empty-box" role="note"><b>{NOT_READY_TEXT}</b><span>{isAdmin ? '결재 통계를 읽지 못했습니다' : '결재 통계는 회사 관리자에게 보입니다'}</span></div>}
        </article>

        {/* ④ 결재 대기함 — 시안 hr-payroll-approval-admin 「결재 대기함」 */}
        <article className="apb-card apb-inboxcard" aria-label="결재 대기함">
          <div className="apb-ch">
            <h2>결재 대기함</h2><span className="apb-sub num">내 차례 {loading && !counts ? '—' : waitCount}건</span>
            <button type="button" className="apb-btn apb-btn--line apb-btn--sm" onClick={() => pick('wait')}>전체 보기</button>
          </div>
          {wait.length === 0 ? (
            <div className="apb-empty-box" role="note"><b>없음</b><span>{loading ? '불러오는 중…' : '내 차례인 문서가 없습니다'}</span></div>
          ) : (
            <ul className="apb-inbox">
              {wait.slice(0, 4).map((d) => (
                <li key={d.id}>
                  <Link className="lk" to={`/approval/doc/${d.id}`} aria-label={`${d.title} 열기`} />
                  <span className="av" aria-hidden><Inbox /></span>
                  <div className="tx"><b>{d.title}</b><span>{typeName(d.type)} · {ago(draftedOn(d))}</span></div>
                  {d.isUrgent
                    ? <span className="apb-badge apb-badge--bad">긴급</span>
                    : <span className="apb-badge apb-badge--pur">{typeName(d.type)}</span>}
                  <div className="acts"><Link className="apb-btn apb-btn--sm" to={`/approval/doc/${d.id}`}>검토</Link></div>
                </li>
              ))}
            </ul>
          )}
        </article>
      </section>

      <div className="apb-work">
        {/* 결재함 트리 — 원본 approval-management 의 콘텐츠 안 보조 패널 */}
        <aside className="apb-card apb-tree" aria-label="결재함">
          <div className="apb-ch"><h2>결재함</h2><span className="apb-sub">내 문서함 넷에서 가른다</span></div>
          <label className="apb-tsearch"><span className="apb-sr">결재함 이름 찾기</span><Search aria-hidden />
            <input type="search" value={treeQ} placeholder="결재함 이름" onChange={(e) => setTreeQ(e.target.value)} /></label>
          <button type="button" className={`apb-node apb-node--root${box === 'all' ? ' is-on' : ''}`} aria-pressed={box === 'all'} onClick={() => pick('all')}>
            <span className="nm">전체 문서</span><em className="num">{loading ? '' : count('all')}</em>
          </button>
          {GROUPS.map((g) => {
            const boxes = g.boxes.filter((b) => matchTree(BOX_LABEL[b]) || matchTree(g.label));
            if (boxes.length === 0) return null;
            const isOpen = open[g.label] !== false || !!treeQ.trim();
            return (
              <div className="apb-grp" key={g.label}>
                <button type="button" className="apb-grph" aria-expanded={isOpen} onClick={() => setOpen((s) => ({ ...s, [g.label]: !isOpen }))}>
                  <ChevronDown aria-hidden className={isOpen ? '' : 'is-closed'} /><i aria-hidden />{g.label}
                </button>
                {isOpen && (
                  <ul>
                    {boxes.map((b) => (
                      <li key={b}>
                        <button type="button" className={`apb-node${box === b ? ' is-on' : ''}`} aria-pressed={box === b} onClick={() => pick(b)}>
                          <span className="nm">{BOX_LABEL[b]}</span>
                          <em className="num">{b === 'dept' ? '—' : serverCount(b) ?? (loading ? '' : count(b))}</em>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </aside>

        {/* 전자결재 문서 표 — 시안 hr-payroll-approval-admin 「업무 보드 › 전자결재 문서」 */}
        <section className="apb-card apb-list" aria-label="문서 목록">
          <div className="apb-ch">
            <h2>문서 목록</h2>
          </div>

          {/* ①②③④ 툴바 — 검색 · 조건필터 · 지우기 · 내려받기를 **표 바로 위 한 줄**로 */}
          <ApprovalFilterBar
            filters={filters}
            onChange={setFilters}
            open={condOpen}
            onToggleOpen={() => setCondOpen((v) => !v)}
            onClear={() => setFilters(clearFilters)}
            downloadDisabled
            downloadTitle={`문서 목록 내려받기 — ${NOT_READY_TEXT}`}
          />

          {/* 무엇이 걸렸나 — 숫자 배지는 「몇 개」, 칩은 「무엇이」 */}
          <ApprovalFilterChips chips={chips} />

          {/* 조건 상세 — ⚠ 여는 «방식»(펼침 · 서랍 · 모달)은 사용자 확인 대기. 지금은 표 위에서 펼친다. */}
          {condOpen && (
            <div className="apf-panel" id="apf-panel">
              <div className="apf-grid">
                <label className="apf-field">
                  <span>상태</span>
                  <select
                    value={filters.status ?? ''}
                    onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value || null }))}
                  >
                    <option value="">전체</option>
                    <option value="IN_PROGRESS">진행 중</option>
                    <option value="APPROVED">완료</option>
                    <option value="REJECTED">반려</option>
                  </select>
                </label>
                {/* ⛔ 아래 넷은 **서버가 아직 안 받는다**. 그리되 «보내지 않는다»(approval-filters.ts).
                    켜 두면 400 이 아니라 조용히 200 이라 사용자가 틀린 목록을 믿게 된다. */}
                <label className="apf-field">
                  <span>기간<PendingTag /></span>
                  <select disabled><option>최근 3개월</option></select>
                </label>
                <label className="apf-field">
                  <span>문서 종류<PendingTag /></span>
                  <select disabled><option>전체</option></select>
                </label>
                <label className="apf-field">
                  <span>기안자<PendingTag /></span>
                  <input type="text" disabled placeholder="이름으로 찾기" />
                </label>
                <label className="apf-field">
                  <span>기안 부서<PendingTag /></span>
                  <select disabled><option>전체</option></select>
                </label>
              </div>
              <div className="apf-panel-foot">
                <span className="apf-panel-note">
                  흐린 칸은 서버가 아직 받지 않는 조건입니다 — 켜지면 그때 보냅니다
                </span>
                <button type="button" className="apf-btn" onClick={() => setFilters(clearFilters)} disabled={!hasActiveFilters(filters)}>
                  {CLEAR_FILTERS_LABEL}
                </button>
              </div>
            </div>
          )}

          <span className="apb-path">{pathText}</span>

          {listLoading && shown.length === 0 ? (
            <p className="apb-path">불러오는 중…</p>
          ) : shown.length === 0 ? (
            <div className="apb-empty">
              <Inbox aria-hidden />
              <b>{box === 'dept' ? '부서 결재함 문서 목록 — 데이터 준비 중' : '이 결재함에는 표시할 문서가 없습니다'}</b>
              <span>{box === 'dept' ? '서버가 부서 결재함의 문서를 아직 주지 않습니다' : needle ? '검색어를 지우거나 다른 결재함을 고르세요' : '다른 결재함을 고르세요'}</span>
              {/* ★ 정본 P3 「로딩 · 빈 상태」 — 「결과 0 (필터)」에는 `no-result` + **필터 지우기**가 서야 한다.
                  종전에는 이 단추가 없어서, 조건을 잘못 건 사람이 빠져나올 길이 없었다.
                  ★ 하는 일은 툴바의 것과 **똑같다** — 조건만 지우고 **검색어는 남긴다**.
                    같은 글자가 자리마다 다른 일을 하면 누르는 사람이 무엇이 일어날지 모른다.
                  ★ 검색어 하나만으로 0건이면 이 단추는 안 선다 — 이제 **검색칸이 표 바로 위**라
                    지우는 길이 한 걸음이다(이 개편의 값이 여기서도 나온다). */}
              {box !== 'dept' && hasActiveFilters(filters) && (
                <button
                  type="button"
                  className="apf-btn apf-empty-act"
                  onClick={() => setFilters(clearFilters)}
                >
                  <RotateCcw aria-hidden />
                  {CLEAR_FILTERS_LABEL}
                </button>
              )}
            </div>
          ) : (
            <div className="apb-tw">
              <table className="apb-table">
                <thead><tr><th style={{ width: 44 }}><span className="apb-sr">선택</span></th><th className="c-no">문서번호</th><th>제목</th><th className="c-who">기안자 · 부서</th><th>결재선</th><th>기안일</th><th>상태</th></tr></thead>
                <tbody>
                  {shown.map((d) => {
                    const total = Math.max(1, d.totalSteps);
                    const done = d.status === 'APPROVED' ? total : Math.max(0, Math.min(total, d.currentStep - 1));
                    const mark = approvalStatusMark(d.status, Boolean(d.waiting?.mine));
                    return (
                      <tr key={d.id} className={checked.has(d.id) ? 'is-checked' : undefined}
                        onClick={(e) => { if (!(e.target as HTMLElement).closest('.apb-cktd')) navigate(`/approval/doc/${d.id}`); }}>
                        {/* 체크 칸은 칸 전체가 이동하지 않는다 — 상자 밖을 눌러도 문서로 가던 것(리뷰 #1246 메모) */}
                        <td className="apb-cktd"><input className="apb-ck" type="checkbox" checked={checked.has(d.id)} aria-label={`${d.docNo ?? d.title} 선택`} onChange={() => toggle(d.id)} /></td>
                        <td className="code num c-no">{d.docNo ?? '—'}</td>
                        <td className="title">
                          <Link to={`/approval/doc/${d.id}`} onClick={(e) => e.stopPropagation()}>
                            {d.isUrgent && <span className="apb-badge apb-badge--bad apb-urgent">긴급</span>}{d.title}
                          </Link>
                          {/* 좁은 목록에서만 보인다 — 문서번호 열을 숨긴 대신(CSS @container apb-list) */}
                          <small className="apb-subno num">{d.docNo ?? '문서번호 없음'}{d.drafter?.name ? ` · ${d.drafter.name}` : ''}</small>
                        </td>
                        <td className="c-who">
                          {d.drafter?.name ? (
                            <span className="apb-person">
                              <Avatar userId={Number(d.drafter.id) || 0} name={d.drafter.name} src={d.drafter.avatarUrl} size="xs" />
                              <span className="apb-who">{d.drafter.name}<small>{d.drafter.teamName ?? '—'}</small></span>
                            </span>
                          ) : <span className="apb-who" title={`기안자 이름 · 부서 — ${NOT_READY_TEXT}`}>—<small>{typeName(d.type)}</small></span>}
                        </td>
                        <td>
                          <span className="apb-pips" aria-label={`결재선 ${d.status === 'APPROVED' ? total : d.currentStep}/${total}`}>
                            {Array.from({ length: total }, (_, i) => (
                              <i key={i} className={i < done ? 'on' : d.status === 'REJECTED' && i === done ? 'no' : undefined} />
                            ))}
                            <span className="num">{d.status === 'APPROVED' ? total : d.currentStep}/{total}{d.waiting?.name && d.status === 'IN_PROGRESS' ? ` · ${d.waiting.mine ? '내 차례' : `${d.waiting.name} 대기`}` : ''}</span>
                          </span>
                        </td>
                        <td className="num">{mmdd(draftedOn(d))}</td>
                        <td>{mark.emphasis === 'mine'
                          ? <span className="apb-badge apb-badge--mine">{mark.label}</span>
                          : <span className="apb-stat">{mark.label}</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {checked.size > 0 && (
            <div className="apb-bulk" aria-live="polite">
              <span className="count num">{checked.size}건 선택됨</span>
              <span className="hint">선택한 문서를 한 번에 처리합니다 — 결재선의 내 차례만 반영됩니다</span>
              <div className="end">
                <button type="button" className="apb-btn apb-btn--danger apb-btn--sm" disabled title={`일괄 반려 — ${NOT_READY_TEXT}`}>일괄 반려</button>
                <button type="button" className="apb-btn apb-btn--sm" disabled title={`일괄 승인 — ${NOT_READY_TEXT}`}>일괄 승인</button>
              </div>
            </div>
          )}
          <div className="apb-foot"><span className="num">{shown.length}건 표시</span></div>
        </section>
      </div>
    </div>
  );
}

export default ApprovalBoxPage;
